import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Mail, Phone, User } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { PasswordInput } from "@/components/form/PasswordInput";
import { PasswordStrengthMeter } from "@/components/form/PasswordStrengthMeter";
import { FieldError } from "@/components/form/FieldError";
import { SubmitButton } from "@/components/form/SubmitButton";
import { signupSchema, type SignupValues } from "@/features/auth/schemas";
import { useAuth } from "@/features/auth/AuthContext";
import { formatApiError } from "@/features/auth/formatError";
import { ROUTES } from "@/constants/routes";
import { api } from "@/features/auth/api";
import { useSearchParams } from "react-router-dom";

export default function Signup() {
    const navigate = useNavigate();
    const { register: registerUser } = useAuth();
    const [loading, setLoading] = useState(false);
    const [params] = useSearchParams();
    const [refCode, setRefCode] = useState<string>((params.get("ref") || "").toUpperCase());

    const {
        register,
        handleSubmit,
        watch,
        setValue,
        formState: { errors, isSubmitted },
    } = useForm<SignupValues>({
        resolver: zodResolver(signupSchema),
        defaultValues: {
            fullName: "",
            phone: "",
            email: "",
            password: "",
            confirmPassword: "",
            acceptTerms: false,
        },
        mode: "onBlur",
    });

    const password = watch("password");
    const acceptTerms = watch("acceptTerms");

    const onSubmit = async (values: SignupValues) => {
        setLoading(true);
        try {
            const user = await registerUser({
                full_name: values.fullName.trim(),
                phone: values.phone.trim(),
                email: values.email.trim().toLowerCase(),
                password: values.password,
                confirm_password: values.confirmPassword,
                accept_terms: values.acceptTerms,
            });
            // Optional referral attach — silent failure so signup UX never
            // blocks on a bad/expired code. Reward only fires on the
            // customer's first delivered order (server-enforced).
            const trimmed = refCode.trim().toUpperCase();
            if (trimmed) {
                try {
                    await api.post("/referrals/attach", { code: trimmed });
                } catch {
                    /* ignored */
                }
            }
            toast.success(`Welcome to TSGo, ${user.full_name.split(" ")[0]}!`, {
                description: "Your account is ready. Enjoy fast delivery.",
            });
            navigate(ROUTES.home, { replace: true });
        } catch (err) {
            toast.error(formatApiError(err, "Sign up failed. Please try again."));
        } finally {
            setLoading(false);
        }
    };

    const onInvalid = () => {
        toast.error("Please fix the highlighted fields");
    };

    return (
        <div data-testid="signup-page" className="space-y-8">
            <header>
                <h1 className="text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
                    Create your account
                </h1>
                <p className="mt-2 text-sm text-muted-foreground">
                    Join TSGo and get essentials delivered in minutes.
                </p>
            </header>

            <form
                noValidate
                onSubmit={handleSubmit(onSubmit, onInvalid)}
                aria-label="Signup form"
                className="space-y-5"
            >
                <div className="space-y-1.5">
                    <Label htmlFor="signup-name">Full name</Label>
                    <div className="relative">
                        <User className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            id="signup-name"
                            type="text"
                            autoComplete="name"
                            placeholder="Jane Doe"
                            className="h-11 pl-9"
                            data-testid="signup-name"
                            aria-invalid={!!errors.fullName || undefined}
                            aria-describedby={
                                errors.fullName ? "signup-name-error" : undefined
                            }
                            disabled={loading}
                            {...register("fullName")}
                        />
                    </div>
                    <FieldError
                        id="signup-name-error"
                        message={errors.fullName?.message}
                    />
                </div>

                <div className="grid gap-5 sm:grid-cols-2">
                    <div className="space-y-1.5">
                        <Label htmlFor="signup-phone">Mobile number</Label>
                        <div className="relative">
                            <Phone className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                            <Input
                                id="signup-phone"
                                type="tel"
                                inputMode="tel"
                                autoComplete="tel"
                                placeholder="+91 98765 43210"
                                className="h-11 pl-9"
                                data-testid="signup-phone"
                                aria-invalid={!!errors.phone || undefined}
                                aria-describedby={
                                    errors.phone
                                        ? "signup-phone-error"
                                        : undefined
                                }
                                disabled={loading}
                                {...register("phone")}
                            />
                        </div>
                        <FieldError
                            id="signup-phone-error"
                            message={errors.phone?.message}
                        />
                    </div>

                    <div className="space-y-1.5">
                        <Label htmlFor="signup-email">Email</Label>
                        <div className="relative">
                            <Mail className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                            <Input
                                id="signup-email"
                                type="email"
                                inputMode="email"
                                autoComplete="email"
                                placeholder="you@example.com"
                                className="h-11 pl-9"
                                data-testid="signup-email"
                                aria-invalid={!!errors.email || undefined}
                                aria-describedby={
                                    errors.email
                                        ? "signup-email-error"
                                        : undefined
                                }
                                disabled={loading}
                                {...register("email")}
                            />
                        </div>
                        <FieldError
                            id="signup-email-error"
                            message={errors.email?.message}
                        />
                    </div>
                </div>

                <div className="space-y-1.5">
                    <Label htmlFor="signup-password">Password</Label>
                    <PasswordInput
                        id="signup-password"
                        placeholder="Create a strong password"
                        autoComplete="new-password"
                        data-testid="signup-password"
                        aria-describedby="signup-password-strength signup-password-error"
                        error={!!errors.password}
                        disabled={loading}
                        {...register("password")}
                    />
                    <PasswordStrengthMeter
                        id="signup-password-strength"
                        password={password ?? ""}
                    />
                    <FieldError
                        id="signup-password-error"
                        message={errors.password?.message}
                    />
                </div>

                <div className="space-y-1.5">
                    <Label htmlFor="signup-confirm">Confirm password</Label>
                    <PasswordInput
                        id="signup-confirm"
                        placeholder="Re-enter your password"
                        autoComplete="new-password"
                        data-testid="signup-confirm"
                        aria-describedby={
                            errors.confirmPassword
                                ? "signup-confirm-error"
                                : undefined
                        }
                        error={!!errors.confirmPassword}
                        disabled={loading}
                        {...register("confirmPassword")}
                    />
                    <FieldError
                        id="signup-confirm-error"
                        message={errors.confirmPassword?.message}
                    />
                </div>

                <div className="space-y-1">
                    <div className="flex items-start gap-2">
                        <Checkbox
                            id="signup-terms"
                            checked={!!acceptTerms}
                            onCheckedChange={(v) =>
                                setValue("acceptTerms", v === true, {
                                    shouldValidate: isSubmitted,
                                })
                            }
                            data-testid="signup-terms"
                            disabled={loading}
                            aria-invalid={!!errors.acceptTerms || undefined}
                            aria-describedby={
                                errors.acceptTerms
                                    ? "signup-terms-error"
                                    : undefined
                            }
                            className="mt-0.5"
                        />
                        <Label
                            htmlFor="signup-terms"
                            className="cursor-pointer text-sm font-normal leading-snug text-foreground/80"
                        >
                            I agree to TSGo&apos;s{" "}
                            <span className="font-semibold text-brand hover:underline">
                                Terms of Service
                            </span>{" "}
                            and{" "}
                            <span className="font-semibold text-brand hover:underline">
                                Privacy Policy
                            </span>
                            .
                        </Label>
                    </div>
                    <FieldError
                        id="signup-terms-error"
                        message={errors.acceptTerms?.message}
                    />
                </div>

                <div className="space-y-1">
                    <Label htmlFor="signup-referral" className="text-sm font-semibold text-foreground/80">
                        Referral code <span className="text-muted-foreground">(optional)</span>
                    </Label>
                    <Input
                        id="signup-referral"
                        value={refCode}
                        onChange={(e) => setRefCode(e.target.value.toUpperCase())}
                        placeholder="TSGO-XXXXXX"
                        data-testid="signup-referral"
                        disabled={loading}
                        className="uppercase"
                    />
                    <p className="text-xs text-muted-foreground">
                        Enter a friend's code — you both get ₹50 on your first delivered order.
                    </p>
                </div>

                <div className="pt-1">
                    <SubmitButton
                        type="submit"
                        loading={loading}
                        loadingText="Creating account…"
                        data-testid="signup-submit"
                    >
                        Create account
                    </SubmitButton>
                </div>
            </form>

            <p className="text-center text-sm text-muted-foreground">
                Already have an account?{" "}
                <Link
                    to={ROUTES.login}
                    className="font-semibold text-brand hover:underline"
                    data-testid="signup-goto-login"
                >
                    Sign in
                </Link>
            </p>
        </div>
    );
}
